import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart, AnimatedLine, lineElementClasses, markElementClasses } from '@mui/x-charts/LineChart';
import { useChartId, useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import Skeleton from '@mui/material/Skeleton';

function CustomAnimatedLine(props) {
    const { limit, sxBefore, sxAfter, ...other } = props;
    const { top, bottom, height, left, width } = useDrawingArea();
    const scale = useXScale();
    const chartId = useChartId();

    if (limit === undefined) {
        return <AnimatedLine {...other} />;
    }

    const limitPosition = scale(limit);

    if (limitPosition === undefined) {
        return <AnimatedLine {...other} />;
    }

    const clipIdleft = `${chartId}-${props.ownerState.id}-line-limit-${limit}-1`;
    const clipIdRight = `${chartId}-${props.ownerState.id}-line-limit-${limit}-2`;

    return (
        <React.Fragment>
            <clipPath id={clipIdleft}>
                <rect x={left} y={0} width={limitPosition - left} height={top + height + bottom} />
            </clipPath>
            <clipPath id={clipIdRight}>
                <rect x={limitPosition} y={0} width={left + width - limitPosition} height={top + height + bottom} />
            </clipPath>
            <g clipPath={`url(#${clipIdleft})`}>
                <AnimatedLine {...other} sx={sxBefore} />
            </g>
            <g clipPath={`url(#${clipIdRight})`}>
                <AnimatedLine {...other} sx={sxAfter} />
            </g>
        </React.Fragment>
    );
}

export default function AnnualCount({ chartData, loading }) {
    const theme = useTheme();

    const chartSetting = {
        height: 300,
        margin: { top: 20, bottom: 30, left: 60, right: 20 },
        sx: {
            '& .MuiChartsAxis-left .MuiChartsAxis-line': {
                stroke: theme.palette.text.secondary,
            },
            '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
                stroke: theme.palette.text.secondary,
            },
            '& .MuiChartsAxis-left .MuiChartsAxis-tick': {
                stroke: theme.palette.text.secondary,
            },
            '& .MuiChartsAxis-bottom .MuiChartsAxis-tick': {
                stroke: theme.palette.text.secondary,
            },
            '& .MuiChartsAxis-label': {
                fill: theme.palette.text.secondary,
            },
            [`.${lineElementClasses.root}, .${markElementClasses.root}`]: {
                strokeWidth: 1,
            },
            '.MuiLineElement-series-pvId': {
                strokeDasharray: '5 5',
            },
            '.MuiLineElement-series-uvId': {
                strokeDasharray: '3 4 5 2',
            },
            [`.${markElementClasses.root}:not(.${markElementClasses.highlighted})`]: {
                fill: '#fff',
            },
            [`& .${markElementClasses.highlighted}`]: {
                stroke: 'none',
            },
        }
    };

    return (
        <Card variant="outlined" sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Annual EV Registration Trends
                </Typography>
                <Stack sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
                        Annual EV registrations showing growth trends over time
                    </Typography>
                </Stack>
                {loading ? (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={300}
                        sx={{ borderRadius: 1, bgcolor: theme.palette.background.paper }}

                    />
                ) : (
                    <LineChart
                        {...chartSetting}
                        series={[{
                            data: chartData?.map(d => d.count) || [],
                            label: 'Annual Registrations',
                            valueFormatter: (value) => `${(value || 0).toLocaleString()} vehicles`,
                            color: theme.palette.primary.main,
                            showMark: true,
                            type: 'line',
                            id: 'uvId'
                        }]}
                        xAxis={[{
                            data: chartData?.map(d => d.year) || [],

                            valueFormatter: (value) => value.toString(),
                            // scaleType: 'band'
                        }]}
                        yAxis={[{
                            valueFormatter: (value) => value?.toLocaleString() || '0',
                            min: 0,
                        }]}
                        slots={{ line: CustomAnimatedLine }}
                        height={300}

                        slotProps={{
                            legend: { hidden: true },
                            line: {
                                limit: 2024,
                                sxAfter: { strokeDasharray: '10 5' }
                            }
                        }}
                    />
                )}
            </CardContent>
        </Card>
    );
}

AnnualCount.propTypes = {
    chartData: PropTypes.arrayOf(
        PropTypes.shape({
            year: PropTypes.number.isRequired,
            count: PropTypes.number.isRequired,
        })
    ).isRequired,
    loading: PropTypes.bool
};
